import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { merge, of as observableOf, Subscription } from 'rxjs';
import { catchError, delay, map, startWith, switchMap, tap } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { RegelDetailsComponent } from '../regel-details/regel-details.component';
import { IPreSalesRow, LeverancierStatus, LeverancierStatusString, RegelsService } from '../regels.service';
import { SharedNotificationService } from '../shared-notification.service';
import { AutocompleteService, ISelectItem, IVbnFeatureValue } from '../autocomplete.service';

@Component({
    selector: 'app-regels-table',
    templateUrl: './regels-table.component.html',
    styleUrls: ['./regels-table.component.css']
})
export class RegelsTableComponent {
    displayedColumns: string[] = [
        'regelNr',
        'debiteurNaam',
        'thumbnail',
        'artikelNr',
        'productNaam',
        'potmaat',
        'hoogte',
        'afleverperiodeVan',
        'deadlineInkoopDate',
        'aantalGevraagd',
        'inkoperNaam',
        'status',
        'acties',
        'isChanged',
    ];
    data: IPreSalesRow[] = [];
    dataSource: MatTableDataSource<IPreSalesRow> = new MatTableDataSource<IPreSalesRow>();


    saveButtonIsEnabled: boolean = false;

    @Input() onlyShowArchivedRegels: boolean;
    selectedEan: number;
    @Input() version: number;

    regelsFilter: EventEmitter<any> = new EventEmitter();

    @Output() onSaveEvent = new EventEmitter();

    @ViewChild(MatSort, { static: false }) sort: MatSort;

    onSupplierChangeSubscription: Subscription;
    leverancierStatusType = LeverancierStatusString;
    leverancierStatusKeys = Object.values(LeverancierStatusString).filter(value => typeof value === 'number');
    supplierAssignedIDs: ISelectItem[];
    vbnFeatureValues: IVbnFeatureValue[];


    constructor(
        private _regelsService: RegelsService,
        private _snackBar: MatSnackBar,
        private _dialog: MatDialog,
        private _details: MatDialog,
        private _notificationService: SharedNotificationService,
        private _autocompleteService: AutocompleteService) {
    }

    ngOnInit() {
        this.dataSource.sort = this.sort;

        // check if selectedEan is correct
        if (this.selectedEan == undefined) {
            let selectedEan = this._notificationService.getOnSupplierChange;
            if (selectedEan != null && selectedEan != undefined) {
                this.selectedEan = selectedEan;
            }
        }
        this._autocompleteService.getSupplierAssignedIDs('', this.selectedEan).subscribe((val => this.supplierAssignedIDs = val.data));
        this._autocompleteService.getVbnFeatureValues('S02').subscribe((val => this.vbnFeatureValues = val));

        merge(this.regelsFilter, this.onSaveEvent)
            .pipe(
                startWith({}),
                delay(0),
                switchMap(() => {
                    this._notificationService.setIsLoadingRowResults(true);
                    return this._regelsService.getSupplierRequest(this.selectedEan);
                }),
                map(data => {
                    // Flip flag to show that loading has finished.
                    this._notificationService.setIsLoadingRowResults(false);

                    return data.items;
                }),
                catchError(() => {
                    this._notificationService.setIsLoadingRowResults(false);

                    // Return empty data.
                    return observableOf([]);
                })
            ).subscribe(data => {
                this.data = data;

                this.data.filter(row => row.leverancierDetails).forEach((row) => {

                    let vbnFeatureValue = this.vbnFeatureValues.find(vbn => vbn.featureValue.replace(/^0+/, '') == row.leverancierDetails.hoogte?.toString());
                    if (vbnFeatureValue)
                        row.vbnHoogte = vbnFeatureValue.description;
                });

                if (this.onlyShowArchivedRegels) {
                    this.dataSource.data = this.data.filter(d => d.status != LeverancierStatus.Nieuw && d.confirmationDate != null);
                } else {
                    this.dataSource.data = this.data.filter(d => d.status == LeverancierStatus.Nieuw || d.confirmationDate == null);
                }
                this.dataSource.sort = this.sort;

                this.enableDisableSaveButton();
            });

        this.onSupplierChangeSubscription = this._notificationService.onSupplierChangeObservable.subscribe({
            next: (x: number) => {
                this.selectedEan = x;
                this.regelsFilter.emit();
            },
            error: (err: Error) => console.error('Something went wrong setting selectedEan next value. Retrieved an error: ' + err)
        });
    }

    enableDisableSaveButton() {
        this.saveButtonIsEnabled = this.data.filter(d => d.isChanged).length > 0;
    }

    hasChanges() {
        return this.data?.some(r => r.isChanged);
    }

    onChange(item: IPreSalesRow) {
        item.isChanged = true;

        this._regelsService
            .patchStatus(item)
            .subscribe(data => { }, (err) => {
                console.error("Failed! " + err);
                this._snackBar.open('Er is iets fout gegaan tijdens het opslaan. Probeer opnieuw.', null, {
                    duration: 2000,
                    panelClass: ['kp-status-message', 'kp-status-message-error']
                });
            });

        this.enableDisableSaveButton();
    }

    onSave() {
        this._notificationService.setIsLoadingRowResults(true);

        var changedEntities = this.data.filter(d => d.isChanged);

        var numberOfEntities = changedEntities.length;
        if (changedEntities.length) {
            var message = 'Weet u zeker dat u de ' + numberOfEntities + ' gewijzigde regels wilt overdragen aan Hamiplant?';
            if (numberOfEntities === 1) {
                message = 'Weet u zeker dat u de gewijzigde regel wilt overdragen aan Hamiplant?';
            }
            const confirmDialog = this._dialog.open(ConfirmDialogComponent, {
                data: {
                    title: 'Bevestigen',
                    message: message
                }
            });
            confirmDialog.afterClosed().subscribe(result => {
                if (result === true) {
                    changedEntities.forEach((item, index) => {
                        item.confirmationDate = new Date(Date.now());

                        // if status is 0 = NIEUW, then we change the status to voltooid = 1
                        if (item.status === 0) {
                            item.status = 1;
                        }

                        // update item not being changed anymore
                        item.isChanged = false;

                        this._regelsService
                            .update(item)
                            .subscribe(data => {
                                if (index + 1 == numberOfEntities) {
                                    // trigger application to send e-mail with all changed entities as offerte
                                    let ids = changedEntities.map(a => a.id);
                                    this._regelsService.getOfferteExport(ids).subscribe();

                                    this._snackBar.open('Succesvol opgeslagen.', null, {
                                        duration: 2000,
                                        panelClass: ['kp-status-message', 'kp-status-message-success']
                                    });
                                    this._notificationService.setIsLoadingRowResults(false);
                                    this.onSaveEvent.emit();
                                }
                            }, (err) => {
                                console.error("Failed! " + err);
                                this._snackBar.open('Er is iets fout gegaan tijdens het opslaan. Probeer opnieuw.', null, {
                                    duration: 2000,
                                    panelClass: ['kp-status-message', 'kp-status-message-error']
                                });
                            });
                    }
                    );
                }
                else {
                    this._notificationService.setIsLoadingRowResults(false);
                }
            });
        }
        else {
            this._notificationService.setIsLoadingRowResults(false);
        }
    }

    onDetailsOpen(id: string) {
        const buttonElement = document.activeElement as HTMLElement; // Get the currently focused element
        buttonElement.blur(); // Remove focus from the button

        const detailsDialog = this._details.open(RegelDetailsComponent, {
            data: {
                id: id,
            }
        });

        detailsDialog
            .afterClosed()
            .subscribe(obj => {
                // if details component is saved, a RegelDetailsSaveResult object is returned and hence we mark this regel as changed
                if (obj != null && obj != undefined && obj.refreshAllRows) {
                    this.onSaveEvent.emit(true);
                }
                else if (obj?.result != null && obj?.result != undefined) {
                    var item = this.data.find(d => d.id === id);
                    // set status retrieved from component in obj
                    item.status = obj.status;
                    //this.onSaveEvent.emit(true);
                    this.onChange(item);
                }
            });
    }

    ngOnDestroy() {
        if (this.onSupplierChangeSubscription) {
            this.onSupplierChangeSubscription.unsubscribe();
        }
    }

    trackById(index, item) {
        return index;
    }
}
